import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Copy, Check } from "lucide-react";
import { streamPost } from "@/lib/api";
import { Reveal, SectionHeading } from "./Reveal";

const FIELD = "w-full rounded-2xl border border-navy/15 bg-white px-4 py-3 text-base text-navy placeholder:text-navy/60 transition-[border-color,box-shadow] duration-300 focus:border-teal focus:outline-none focus:ring-4 focus:ring-teal/20";

const Field = ({ label, id, children, hint }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block font-heading text-sm font-extrabold text-navy">{label}{hint && <span className="ml-1 font-sans text-xs font-medium text-navy/70">{hint}</span>}</label>
    {children}
  </div>
);

const StoryOutput = ({ story, loading, error }) => {
  const [copied, setCopied] = useState(false);
  const [title, ...rest] = story.split("\n");
  const copy = async () => {
    await navigator.clipboard?.writeText(story);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div data-testid="story-output" className="relative flex min-h-[340px] flex-col rounded-[2rem] bg-white p-7 shadow-[0_30px_70px_-35px_rgba(18,52,90,0.5)] sm:p-9">
      <p className="eyebrow">Keepsake story</p>
      {error && <p data-testid="story-error" role="alert" className="mt-5 rounded-2xl bg-coral/25 p-4 text-sm text-navy">{error}</p>}
      {!story && !error && (
        <p className="mt-5 text-base leading-relaxed text-navy/70">{loading ? "Writing something lovely…" : "Your story will appear here. Add a few notes and tap Write the story."}</p>
      )}
      {story && (
        <div className="mt-5 flex-1" aria-live="polite">
          <h3 className="font-heading text-2xl font-extrabold text-navy">{title.replace(/^#+\s*|\*\*/g, "")}</h3>
          <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-navy/85">{rest.join("\n").replace(/\*\*/g, "").trim()}{loading && <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-teal align-middle" />}</p>
        </div>
      )}
      {story && !loading && (
        <button type="button" data-testid="story-copy-button" onClick={copy} className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border-2 border-navy px-4 py-2 font-heading text-sm font-extrabold text-navy transition-[background-color] duration-300 hover:bg-navy/5">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy story"}
        </button>
      )}
      <p className="mt-6 text-xs leading-relaxed text-navy/70">Written by AI from your notes. Please review before you keep it. Nothing you type here is saved.</p>
    </div>
  );
};

export const StoryHelper = () => {
  const [form, setForm] = useState({ child_name: "", age: "", kind: "", notes: "" });
  const [story, setStory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    setStory("");
    try {
      await streamPost("/ai/story", { ...form, child_name: form.child_name || null, age: form.age || null }, setStory);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="story-helper" aria-labelledby="story-heading" className="relative px-5 py-24 sm:px-8 lg:py-36">
      <div className="mx-auto max-w-7xl">
        <SectionHeading id="story-heading" eyebrow="Try it now" title="Turn a few notes into a keepsake story." intro="Tell us what they made and what they said about it. Our AI story helper writes a short story you can keep with the memory." />
        <div className="mt-14 grid gap-8 lg:mt-16 lg:grid-cols-2 lg:gap-12">
          <Reveal>
            <form onSubmit={submit} data-testid="story-form" className="space-y-5 rounded-[2rem] bg-[#F4EFE4] p-7 sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Child's first name" hint="(optional)" id="story-name">
                  <input id="story-name" data-testid="story-name-input" maxLength={40} value={form.child_name} onChange={set("child_name")} placeholder="e.g. Maya" className={FIELD} />
                </Field>
                <Field label="Age" hint="(optional)" id="story-age">
                  <input id="story-age" data-testid="story-age-input" maxLength={20} value={form.age} onChange={set("age")} placeholder="e.g. 5" className={FIELD} />
                </Field>
              </div>
              <Field label="What did they make?" id="story-kind">
                <input id="story-kind" data-testid="story-kind-input" required minLength={2} maxLength={60} value={form.kind} onChange={set("kind")} placeholder="e.g. Crayon drawing of our house" className={FIELD} />
              </Field>
              <Field label="The story behind it" id="story-notes">
                <textarea id="story-notes" data-testid="story-notes-input" required minLength={5} maxLength={600} rows={5} value={form.notes} onChange={set("notes")} placeholder="What did they say about it? Where were they when they made it?" className={`${FIELD} resize-none`} />
              </Field>
              <p className="text-xs text-navy/70">Please don't include last names, schools or other personal details.</p>
              <motion.button
                type="submit"
                data-testid="story-submit-button"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-teal font-heading font-extrabold text-white shadow-[0_14px_34px_-12px_rgba(12,166,162,0.6)] transition-[background-color] duration-300 hover:bg-teal-dark disabled:opacity-70 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                {loading ? "Writing…" : "Write the story"}
              </motion.button>
            </form>
          </Reveal>
          <Reveal delay={0.1}>
            <StoryOutput story={story} loading={loading} error={error} />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
